import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ListFilter } from 'lucide-react'
import s from './FilterDisclosure.module.css'

type Motion = 'closed' | 'opening' | 'open' | 'closing'

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

function animationEnd(animation: Animation): number {
  const timing = animation.effect?.getComputedTiming()
  return (timing?.delay ?? 0) + (Number(timing?.duration) || 0)
}

/**
 * Lenses stay visible; the secondary filters wait behind a narrow toggle at the
 * end of the lens row and unfold from under it (List filters v1): the clip grows
 * from 0 to the body's measured height while the body slides down from beneath
 * the lenses and fades in during the first --dur-fast; folding reverses, fading
 * last. Filter values persist while the row is collapsed. A reduced-motion viewer,
 * a mount and a re-render get the settled state in place.
 */
export function FilterDisclosure({
  label,
  lenses,
  children,
  activeCount = 0,
  variant = 'workspace',
  defaultOpen = false,
}: {
  label: string
  lenses: ReactNode
  children: ReactNode
  activeCount?: number
  variant?: 'workspace' | 'toolbar'
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [motion, setMotion] = useState<Motion>(defaultOpen ? 'open' : 'closed')
  const motionRef = useRef<Motion>(motion)
  motionRef.current = motion
  const rowRef = useRef<HTMLDivElement>(null)
  const clipRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const tokenRef = useRef(0)
  const firstRender = useRef(true)
  const id = useId()

  const toggle = useCallback(() => setOpen((value) => !value), [])

  // A gapped column would add its gap the moment the row is displayed: cancel it
  // above the row and carry it inside the clip so the content below moves smoothly.
  useLayoutEffect(() => {
    const row = rowRef.current
    const parent = row?.parentElement
    if (!row || !parent) return
    const style = getComputedStyle(parent)
    const gap = parseFloat(style.rowGap)
    const column =
      (style.display.includes('flex') && style.flexDirection.startsWith('column')) ||
      style.display.includes('grid')
    if (column && gap > 0) row.style.setProperty('--lf-gap', `${gap}px`)
    else row.style.removeProperty('--lf-gap')
  }, [])

  // Settle once every running transition under the clip has finished. A
  // transition seeked to its end while paused never resolves its promise, so a
  // polling backstop settles the row while one still moving keeps it waiting.
  const settleAfterMotion = useCallback((token: number, then: () => void) => {
    const clip = clipRef.current
    if (!clip) {
      then()
      return
    }
    clip.getBoundingClientRect()
    const animations = clip
      .getAnimations({ subtree: true })
      .filter((animation) => animation.playState !== 'finished')
    if (animations.length === 0) {
      then()
      return
    }
    let done = false
    const finish = () => {
      if (done || token !== tokenRef.current) return
      done = true
      then()
    }
    void Promise.allSettled(animations.map((animation) => animation.finished)).then(finish)
    const longest = Math.max(...animations.map(animationEnd))
    const poll = () => {
      if (done || token !== tokenRef.current) return
      const busy = animations.some(
        (animation) =>
          animation.playState === 'running' ||
          animation.pending ||
          (animation.playState === 'paused' &&
            (Number(animation.currentTime) || 0) < animationEnd(animation)),
      )
      if (busy) window.setTimeout(poll, longest + 120)
      else finish()
    }
    window.setTimeout(poll, longest + 120)
  }, [])

  // Step 1 — react to the toggle: pin the geometry, then move.
  useLayoutEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    const row = rowRef.current
    const clip = clipRef.current
    const body = bodyRef.current
    const current = motionRef.current
    const token = ++tokenRef.current
    if (!row || !clip || !body) {
      setMotion(open ? 'open' : 'closed')
      return
    }
    const instant = reducedMotion()
    if (open) {
      if (current === 'open' || current === 'opening') return
      if (instant) {
        row.style.removeProperty('--lf-h')
        setMotion('open')
        return
      }
      // The row rendered visible with the folded geometry; measure the body's
      // natural height and let the transition run from there. A fold being
      // reversed keeps its pinned height.
      if (current !== 'closing') row.style.setProperty('--lf-h', `${body.offsetHeight}px`)
      row.getBoundingClientRect()
      setMotion('opening')
    } else {
      if (current === 'closed' || current === 'closing') return
      if (instant) {
        row.style.removeProperty('--lf-h')
        setMotion('closed')
        return
      }
      if (current === 'open') {
        // auto → px first, so the height can fold.
        row.style.setProperty('--lf-h', `${clip.offsetHeight}px`)
        row.getBoundingClientRect()
      }
      setMotion('closing')
    }
    void token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Step 2 — the moving states settle once their transitions end.
  useEffect(() => {
    if (motion !== 'opening' && motion !== 'closing') return
    const token = tokenRef.current
    const next: Motion = motion === 'opening' ? 'open' : 'closed'
    settleAfterMotion(token, () => {
      rowRef.current?.style.removeProperty('--lf-h')
      setMotion(next)
    })
  }, [motion, settleAfterMotion])

  const hidden = !open && motion === 'closed'

  return (
    <div className={`${s.root} ${variant === 'toolbar' ? s.toolbar : ''}`}>
      <div className={s.lenses}>
        <div className={s.tabs}>{lenses}</div>
        <button
          type="button"
          className={s.toggle}
          aria-label={activeCount > 0 ? `${label}, ${activeCount} active` : label}
          title="Filters"
          aria-expanded={open}
          aria-controls={id}
          data-active={activeCount > 0 || undefined}
          onClick={toggle}
        >
          <ListFilter size={16} aria-hidden />
          {activeCount > 0 && (
            <span className={s.count} aria-hidden="true">
              {activeCount}
            </span>
          )}
        </button>
      </div>
      <div id={id} ref={rowRef} className={s.filters} hidden={hidden} data-state={motion}>
        <div ref={clipRef} className={s.clip}>
          <div ref={bodyRef} className={s.body}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
